import {inject, Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {JwtHelperService} from '@auth0/angular-jwt';

import {Router} from "@angular/router";
import {AuthControllerService} from "../../api";
import {StateConfigService} from "../state/state-config.service";

/**
 * Service for Authentication.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private jwtHelper = inject(JwtHelperService);
  private authControllerService = inject(AuthControllerService);
  private stateConfig = inject(StateConfigService);


  /**
   * Logs In using the given data
   * @param _username to login
   * @param _password to login
   */
  // tslint:disable-next-line:variable-name
  //TODO: change to authServiceController
  login(_username: string, _password: string): Observable<boolean> {
    const dataSubject = new Subject<boolean>();

    this.authControllerService.login({
      username: _username,
      password: _password
    }).subscribe(next => {


      if(next.mfaRequired) {
        this.router.navigate(['/mfa'], { state: { response: next } });
        return;
      }

      if (next.pendingDeletion) {
        this.router.navigate(['/reactivateAccount'], { state: { response: next } });
        return;
      }

      // TODO: Disable because PWA disabled
      //this.pushNotificationsService.subscribeToNotification();

      window.localStorage.setItem('token', next.token!);
      this.stateConfig.start();
      dataSubject.next(true);
    }, error => {
      localStorage.removeItem('token');
      sessionStorage.clear();
      dataSubject.error(false);
    });

    return dataSubject.asObservable();
  }


  loginMfa(_username: string, _password: string, _mfaCode: string): Observable<boolean> {
    const dataSubject = new Subject<boolean>();

    this.authControllerService.verify2FA({
      username: _username,
      password: _password,
      verificationCode: _mfaCode
    }).subscribe(next => {

      if (next.pendingDeletion) {
        this.router.navigate(['/reactivateAccount'], { state: { response: next } });
        return;
      }

      window.localStorage.setItem('token', next.token!);
      this.stateConfig.start();
      dataSubject.next(true);
    }, error => {
      localStorage.removeItem('token');
      sessionStorage.clear();
      dataSubject.error(false);
    });

    return dataSubject.asObservable();

  }

  /**
   * Nimmt die Löschung eines zur Löschung vorgemerkten Kontos zurück und meldet an.
   *
   * <p>Das Reaktivierungs-Token stammt aus der Login- bzw. 2FA-Antwort und ist kein Access-Token --
   * es wandert deshalb bewusst nicht in den localStorage, sondern nur in diesen einen Request. Erst
   * dessen Antwort trägt das Token, mit dem die App arbeitet.
   */
  reactivateAccount(_reactivationToken: string): Observable<boolean> {
    const dataSubject = new Subject<boolean>();

    this.authControllerService.reactivateAccount(_reactivationToken).subscribe(next => {

      window.localStorage.setItem('token', next.token!);
      this.stateConfig.start();
      dataSubject.next(true);
    }, error => {
      localStorage.removeItem('token');
      sessionStorage.clear();
      dataSubject.error(false);
    });

    return dataSubject.asObservable();
  }

  /**
   * Ob eine Sitzung besteht.
   *
   * <p>Bewusst ohne Ablaufprüfung: Das Access-Token lebt nur wenige Minuten, die Sitzung dahinter
   * (Refresh-Cookie) bis zu 30 Tage. Ein abgelaufenes Token erneuert der {@link TokenRefreshService}
   * beim nächsten Request; scheitert das, meldet er ab und das Token ist weg. Mit Ablaufprüfung
   * fiele die Oberfläche nach ein paar Minuten ohne Request in den abgemeldeten Zustand.
   */
  isLoggedIn(): boolean {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }
    try {
      this.jwtHelper.decodeToken(token);
      return true;
    } catch (e) {
      // Kein JWT – damit kann kein Request gelingen.
      console.warn('Ungültiges Access-Token im localStorage, es wird abgemeldet:', e);
      this.logout();
      return false;
    }
  }

  /**
   * Meldet ab: widerruft die Sitzung im Backend (Refresh-Cookie) und löscht die lokalen Daten.
   *
   * <p>Die lokalen Daten werden in jedem Fall gelöscht, auch wenn das Backend nicht erreichbar ist.
   * Dann bleibt nur das Refresh-Token bis zu seinem Ablauf gültig; das wird gemeldet.
   */
  logout(): void {
    // withCredentials setzt der AuthInterceptor – ohne Cookie wüsste das Backend nicht, welche
    // Sitzung es widerrufen soll.
    this.authControllerService.logout().subscribe({
      error: (error: unknown) =>
        console.error('Abmelden im Backend fehlgeschlagen, die Sitzung bleibt bis zu ihrem Ablauf gültig:', error)
    });
    this.clearStorage();
    this.stateConfig.stop();
  }

  clearStorage(): void {
    const lastSeenVersion = localStorage.getItem('lastSeenVersion');

    localStorage.removeItem('token')
    sessionStorage.removeItem('token')

    localStorage.clear()
    sessionStorage.clear()

    if (lastSeenVersion) {
      localStorage.setItem('lastSeenVersion', lastSeenVersion);
    }
  }

}
